import en from './translations/en.json';
import hu from './translations/hu.json';
import sr from './translations/sr.json';

/**
 * UI text for the backoffice, in the three languages hotel staff work in.
 *
 * This is LABELS ONLY. Nothing a guest typed is translated — a name, a
 * birthplace and a document number are recorded once, in the script of the
 * travel document they were read from, and the CMS stores them that way.
 *
 * No library. `next-intl` and `react-i18next` were both considered: the first
 * wants a `[locale]` URL segment the backoffice has no use for, and the second
 * needs a client provider, which does not help the two server components that
 * render these screens. A plain object crosses the server/client boundary as
 * JSON and works identically on both sides.
 *
 * English is the source of truth for the SHAPE — `Translations` is `typeof en`,
 * so a key missing from one language, or a typo at a call site, is a build
 * error rather than a string that silently renders as its own key.
 */

export type Translations = typeof en;

export type Locale = 'en' | 'sr' | 'hu';

export const locales: Locale[] = ['en', 'sr', 'hu'];

export const DEFAULT_LOCALE: Locale = 'en';

/** What each language is called in itself, for the switcher. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  sr: 'Srpski',
  hu: 'Magyar',
};

const DICTIONARIES: Record<Locale, Translations> = {
  en,
  // The two translations are structurally identical to English by construction:
  // `Translations` is `typeof en`, so a missing key fails to compile here.
  sr: sr as Translations,
  hu: hu as Translations,
};

export const isLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (locales as string[]).includes(value);

export const getTranslations = (locale: string | undefined): Translations =>
  isLocale(locale) ? DICTIONARIES[locale] : DICTIONARIES[DEFAULT_LOCALE];

/**
 * Payload speaks `rs` where the rest of the world — and `Intl` — says `sr`.
 *
 * Kept as one function rather than scattered ternaries, because getting it
 * wrong in either direction means silently falling back to English.
 */
export const toPayloadLocale = (locale: Locale): 'en' | 'rs' | 'hu' =>
  locale === 'sr' ? 'rs' : locale;

export const fromPayloadLocale = (locale: string): Locale =>
  locale === 'rs' ? 'sr' : isLocale(locale) ? locale : DEFAULT_LOCALE;

/** Fills `{name}` placeholders. Values are inserted as-is, never parsed. */
export const fill = (template: string, values?: Record<string, string | number>): string =>
  values
    ? template.replace(/\{(\w+)\}/g, (match, key) => (key in values ? String(values[key]) : match))
    : template;

/**
 * The shape of a countable string.
 *
 * `few` is optional because most languages do not have that category — but
 * Serbian does, and leaving it out is exactly the bug this type exists to make
 * visible.
 */
export type PluralForms = {
  one: string;
  few?: string;
  other: string;
};

/**
 * Picks the right plural form for the locale and fills in the count.
 *
 * `Intl.PluralRules` rather than a hand-rolled `count === 1 ? a : b`, because
 * that test is wrong in two of our three languages. Serbian has three
 * categories keyed off the last digits — 1 rezervacija, 2 rezervacije,
 * 5 rezervacija, and again 21 rezervacija — which no simple comparison
 * reproduces. Hungarian has one form and keeps the noun singular after any
 * numeral, so it resolves to `other` every time.
 *
 * The rules live in the platform, so this costs nothing and stays correct if a
 * language is added later.
 */
export const plural = (
  locale: Locale,
  forms: PluralForms,
  count: number,
  values?: Record<string, string | number>
): string => {
  const category = new Intl.PluralRules(locale).select(count);

  const template =
    (category === 'one' && forms.one) || (category === 'few' && forms.few) || forms.other;

  return fill(template, { count, ...values });
};
